//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import React, { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import {
  IDraftDecoratorComponentProps,
  IFacebookPageSuggestion,
} from '@components/molecules/smart-input/smartInputModel';
import { FacebookMentionWrapper } from '@components/molecules/smart-input/mentions/FacebookMentionWrapper';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { extractFacebookPageIdFromTag } from '@utils/social-text-utils/facebookTextUtils';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';

type Props = IDraftDecoratorComponentProps;

export const FacebookPageMention: React.FunctionComponent<Props> = ({
  children,
  decoratedText,
}) => {
  const smartInputContext = useSmartInputContext();

  const [facebookPage, setFacebookPage] = useState<IFacebookPageSuggestion>();
  const [hasErrorInGettingPageMention, setHasErrorInGettingPageMention] =
    useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const debouncedGetPageSuggestion = useDebouncedCallback(
    async (facebookPageMention: string) => {
      // The decorated text will come in with the @[page_id] syntax. This line
      // will grab only the page_id.
      const pageId = extractFacebookPageIdFromTag(facebookPageMention);
      const page = await smartInputContext.mentions?.getFacebookPageById?.(
        pageId,
      );

      setFacebookPage(page);
      setHasErrorInGettingPageMention(!page);
    },
    500,
  );

  // Handle getting the page_id from the page mention syntax when it mounts/changes
  useEffect(() => {
    void debouncedGetPageSuggestion(decoratedText);
  }, [debouncedGetPageSuggestion, decoratedText]);

  return (
    <SmallTooltip
      appendTarget={'body'}
      isDisabled={false}
      target={
        <FacebookMentionWrapper
          hasError={hasErrorInGettingPageMention}
          ref={textRef}
        >
          {children}
        </FacebookMentionWrapper>
      }
      content={
        hasErrorInGettingPageMention || !facebookPage ? (
          'This is an invalid page id and will not successfully tag a page.'
        ) : (
          <>
            {`This will be converted to a mention for `}
            <PrimaryAnchor target='_blank' href={facebookPage.link}>
              {facebookPage.name}
            </PrimaryAnchor>
            {` when posted.`}
          </>
        )
      }
    />
  );
};
